import {
  createContext,
  useContext,
  useId,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from './button'
import { ModalShell } from './modal'

interface DialogContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
  titleId: string
  descriptionId: string
}

const DialogContext = createContext<DialogContextValue | null>(null)

function useDialogContext(): DialogContextValue {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('Dialog components must be used within <Dialog>')
  }
  return context
}

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

function Dialog({ open, onOpenChange, children }: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()

  return (
    <DialogContext.Provider value={{ open, onOpenChange, titleId, descriptionId }}>
      {children}
    </DialogContext.Provider>
  )
}

export interface DialogTriggerProps {
  children?: ReactNode
  className?: string
}

function DialogTrigger({ children, className }: DialogTriggerProps) {
  const { open, onOpenChange } = useDialogContext()

  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => onOpenChange(true)}
      className={cn('inline-flex items-center', className)}
    >
      {children}
    </button>
  )
}

export interface DialogContentProps extends HTMLAttributes<HTMLDivElement> {
  hideCloseButton?: boolean
}

function DialogContent({
  className,
  hideCloseButton = false,
  children,
  ...props
}: DialogContentProps) {
  const { open, onOpenChange, titleId, descriptionId } = useDialogContext()

  return (
    <ModalShell
      open={open}
      onOpenChange={onOpenChange}
      placement="center"
      titleId={titleId}
      descriptionId={descriptionId}
      panelClassName={cn('p-6', className)}
      {...props}
    >
      {children}
      {hideCloseButton ? null : (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 size-8"
          onClick={() => onOpenChange(false)}
          aria-label="Close dialog"
        >
          <X aria-hidden className="size-4" />
        </Button>
      )}
    </ModalShell>
  )
}

function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 flex flex-col space-y-1.5 text-left', className)} {...props} />
}

function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  const { titleId } = useDialogContext()
  return (
    <h2
      id={titleId}
      className={cn('font-display text-lg font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  const { descriptionId } = useDialogContext()
  return (
    <p id={descriptionId} className={cn('text-sm text-muted-foreground', className)} {...props} />
  )
}

export {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
}